import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Schedule as ScheduleIcon,
  Computer as ComputerIcon,
  CloudQueue as CloudIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const HealthMonitoring = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    loadHealthData();
    // Actualiser toutes les 30 secondes
    const interval = setInterval(loadHealthData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadHealthData = async () => {
    try {
      setLoading(true);
      
      const data = await apiService.get('/api/health');
      setHealthData(data);
      setError('');
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Erreur:', error);
      setError(error.message);
      // Données de démonstration en cas d'erreur
      setHealthData({
        status: 'healthy',
        timestamp: new Date().toISOString(),
        uptime: '2d 14h 30m',
        uptimeSeconds: 225000,
        version: '1.0.0',
        port: 3002,
        environment: 'production',
        features: {
          'ai-planning': true,
          'backtracking-algorithm': true,
          'proxy-llm': true,
          'openai': true,
          'database': true,
          'email-service': true,
          'audit-system': true
        },
        memory: {
          used: 156,
          total: 512,
          external: 23,
          unit: 'MB'
        },
        database: {
          status: 'connected',
          size: '2.3 MB',
          tables: 8,
          lastBackup: '2025-08-17T02:00:00Z'
        },
        services: {
          'astreintes-api': { status: 'running', pid: 4132291, memory: '41.3MB' },
          'email-service': { status: 'running', pid: 4132292, memory: '12.1MB' }
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case 'running':
      case 'connected':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
      case 'stopped':
      case 'disconnected':
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="disabled" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
      case 'running':
      case 'connected':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
      case 'stopped':
      case 'disconnected':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) {
      return `${days}j ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const getMemoryUsagePercentage = () => {
    if (!healthData?.memory) return 0;
    return (healthData.memory.used / healthData.memory.total) * 100;
  };

  if (loading && !healthData) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Chargement des informations de santé...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ComputerIcon />
          Santé du Système
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Dernière mise à jour: {format(lastUpdate, 'HH:mm:ss', { locale: fr })}
          </Typography>
          <Tooltip title="Actualiser">
            <IconButton onClick={loadHealthData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {healthData && (
        <>
          {/* Vue d'ensemble */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {getStatusIcon(healthData.status)}
                    <Typography variant="h6">
                      État Général
                    </Typography>
                  </Box>
                  <Typography variant="h4" color={getStatusColor(healthData.status) + '.main'}>
                    {healthData.status === 'healthy' ? 'Sain' : healthData.status}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Version {healthData.version}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ScheduleIcon />
                    <Typography variant="h6">
                      Temps de fonctionnement
                    </Typography>
                  </Box>
                  <Typography variant="h4">
                    {healthData.uptimeSeconds ? formatUptime(healthData.uptimeSeconds) : healthData.uptime}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Port {healthData.port} • {healthData.environment}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <MemoryIcon />
                    <Typography variant="h6">
                      Mémoire
                    </Typography>
                  </Box>
                  <Typography variant="h4">
                    {healthData.memory?.used} {healthData.memory?.unit}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={getMemoryUsagePercentage()}
                    sx={{ mt: 1, mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {getMemoryUsagePercentage().toFixed(1)}% utilisée sur {healthData.memory?.total} {healthData.memory?.unit}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <StorageIcon />
                    <Typography variant="h6">
                      Base de données
                    </Typography>
                  </Box>
                  <Typography variant="h4">
                    {getStatusIcon(healthData.database?.status || 'connected')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Taille: {healthData.database?.size || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tables: {healthData.database?.tables || 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Fonctionnalités */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon />
              Fonctionnalités
            </Typography>
            <Grid container spacing={1}>
              {Object.entries(healthData.features || {}).map(([feature, enabled]) => (
                <Grid item key={feature}>
                  <Chip
                    icon={enabled ? <CheckCircleIcon /> : <ErrorIcon />}
                    label={feature.replace(/-/g, ' ').toUpperCase()}
                    color={enabled ? 'success' : 'error'}
                    variant="outlined"
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Services */}
          {healthData.services && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloudIcon />
                Services
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell>État</TableCell>
                      <TableCell>PID</TableCell>
                      <TableCell>Mémoire</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(healthData.services).map(([serviceName, serviceData]) => (
                      <TableRow key={serviceName}>
                        <TableCell>{serviceName}</TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(serviceData.status)}
                            label={serviceData.status}
                            color={getStatusColor(serviceData.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{serviceData.pid || '-'}</TableCell>
                        <TableCell>{serviceData.memory || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default HealthMonitoring;
